import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ResearchHeader from './ResearchHeader';
import ResearchSidebar from './ResearchSidebar';
import SystemSettingsModal from './SystemSettingsModal';
import '../assets/css/Research.css';

const ResearchLayout = ({ children, pageTitle, hideHeader = false, hideFooter = false }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSystemSettings, setShowSystemSettings] = useState(false);
  const { user } = useAuth();

  // Responsive breakpoint
  const MOBILE_BREAKPOINT = 1200;

  // Handle responsive behavior
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    const newIsMobile = width < MOBILE_BREAKPOINT;
    
    setIsMobile(newIsMobile);
    
    // Auto-open sidebar on desktop, auto-close on mobile
    if (!newIsMobile) {
      setIsSidebarOpen(true);
      // Auto-collapse sidebar for medium screens
      setIsSidebarCollapsed(width < 1400 && width >= MOBILE_BREAKPOINT);
    } else {
      setIsSidebarOpen(false);
      setIsSidebarCollapsed(false);
    }
  }, []);

  // Initialize responsive behavior
  useEffect(() => {
    handleResize();
    setIsLoading(false);
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Handle sidebar toggle
  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setIsSidebarOpen(prev => !prev);
    } else {
      setIsSidebarCollapsed(prev => !prev);
    }
  }, [isMobile]);

  // Handle escape key for mobile sidebar
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isMobile && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isMobile, isSidebarOpen]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isMobile, isSidebarOpen]);

  // Loading state with enhanced appearance
  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-spinner">
            <i className="fas fa-microscope loading-icon"></i>
          </div>
          <h4 className="loading-text">Loading Research Platform</h4>
          <p className="loading-subtext">
            <i className="fas fa-cog fa-spin"></i>
            Please wait while we prepare your dashboard
          </p>
        </div>
      </div>
    );
  }

  // Determine content class based on state
  const getContentClassName = () => {
    let className = 'research-content-wrapper';
    if (isMobile) {
      if (!isSidebarOpen) className += ' sidebar-closed';
    } else {
      if (isSidebarCollapsed) className += ' sidebar-collapsed';
    }
    return className;
  };

  // Get appropriate icon for breadcrumb based on page title
  const getBreadcrumbIcon = (title) => {
    const titleLower = title?.toLowerCase() || '';
    
    if (titleLower.includes('dashboard')) return 'fas fa-tachometer-alt';
    if (titleLower.includes('protocol')) return 'fas fa-clipboard-list';
    if (titleLower.includes('case')) return 'fas fa-folder-open';
    if (titleLower.includes('doctor')) return 'fas fa-user-md';
    if (titleLower.includes('patient')) return 'fas fa-user-injured';
    if (titleLower.includes('report')) return 'fas fa-chart-bar';
    if (titleLower.includes('setting')) return 'fas fa-cog';
    if (titleLower.includes('profile')) return 'fas fa-user';
    if (titleLower.includes('notification')) return 'fas fa-bell';
    if (titleLower.includes('analytic')) return 'fas fa-chart-line';
    if (titleLower.includes('question')) return 'fas fa-question-circle';
    if (titleLower.includes('data')) return 'fas fa-database';
    
    return 'fas fa-file-alt'; // Default icon
  };

  return (
    <div className="research-app">
      {/* Sidebar Component */}
      <ResearchSidebar 
        isOpen={isMobile ? isSidebarOpen : true}
        isCollapsed={!isMobile && isSidebarCollapsed}
        toggleSidebar={toggleSidebar}
        isMobile={isMobile}
        userRole={user?.role ? user.role.toLowerCase().replace(/\s+/g, '_') : undefined}
      />
      
      {/* Main Content Wrapper */}
      <div className={getContentClassName()}>
        {/* Header Component */}
        <ResearchHeader 
          toggleSidebar={toggleSidebar}
          isSidebarOpen={isSidebarOpen}
          isSidebarCollapsed={isSidebarCollapsed}
          isMobile={isMobile}
          user={user}
          pageTitle={pageTitle}
          onOpenSystemSettings={() => setShowSystemSettings(true)}
        />
        
        {/* Main Content Area */}
        <div className="research-main-content">
          <div className="research-content-container">
            {/* Page Title with enhanced styling */}
            {pageTitle && !hideHeader && (
              <div className="research-page-header">
                <h1 className="research-page-title">{pageTitle}</h1>
                <div className="research-breadcrumb-wrapper">
                  <nav aria-label="breadcrumb">
                    <ol className="breadcrumb research-breadcrumb">
                      <li className="breadcrumb-item">
                        <a href={
                          user?.role === 'doctor' ? '/doctor/dashboard' :
                          user?.role === 'Medical Admin' || user?.role === 'medical-admin' ? '/medical-admin/dashboard' :
                          user?.role === 'Program Admin' || user?.role === 'program-admin' ? '/program-admin/dashboard' :
                          user?.role === 'statistician' ? '/statistician/dashboard' :
                          '/'
                        }>
                          <i className="fas fa-home"></i>
                          <span>Dashboard</span>
                        </a>
                      </li>
                      <li className="breadcrumb-item active" aria-current="page">
                        <i className={getBreadcrumbIcon(pageTitle)}></i>
                        <span>{pageTitle}</span>
                      </li>
                    </ol>
                  </nav>
                </div>
              </div>
            )}
            
            {/* Main Content */}
            <main className="research-main" role="main">
              {children}
            </main>
          </div>
          
          {/* Footer with enhanced appearance (optional hide) */}
          {!hideFooter && (
            <footer className="research-footer">
              <div className="research-footer-content">
                <div className="research-footer-brand">
                  <span className="research-footer-logo">
                    <i className="fas fa-heartbeat research-footer-icon"></i>
                    Health.IQ
                  </span>
                  <span className="research-footer-subtitle">Research Platform</span>
                </div>
                <div className="research-footer-copyright">
                  <i className="far fa-copyright"></i>
                  {new Date().getFullYear()} Health.IQ Research. All rights reserved.
                </div>
                <div className="research-footer-links">
                  <a href="https://hlthiq.com/privacy" className="research-footer-link">
                    <i className="fas fa-shield-alt"></i>
                    <span>Privacy Policy</span>
                  </a>
                  <a href="https://hlthiq.com/privacy" className="research-footer-link">
                    <i className="fas fa-file-contract"></i>
                    <span>Terms of Use</span>
                  </a>
                  {/* <a href="#" className="research-footer-link">
                    <i className="fas fa-headset"></i>
                    <span>Contact Support</span>
                  </a> */}
                </div>
              </div>
            </footer>
          )}
        </div>
      </div>
      
      {/* System Settings Modal */}
      <SystemSettingsModal 
        show={showSystemSettings}
        onHide={() => setShowSystemSettings(false)}
      />
    </div>
  );
};

export default ResearchLayout;